import { useMemo } from 'react'
import { createContainer } from 'unstated-next'

import useEmbroideryLogos from '../useEmbroideryLogos'
import { useProductScopedEmbroideryPreferences } from '../useProductScopedEmbroideryPreferences'

import { itemsFromPreferences } from './adapters/itemsFromPreferences'
import { useEmbroideryGarment } from './EmbroideryGarmentProvider'

export type EmbroideryPreferencesProviderProps = {
  /**
   * Whether this render site uses the customer's saved embroidery preferences at all. Off unless
   * asked for: multi-placement has no designs for how preferences should behave when one
   * preference can land in several places, so no surface turns this on yet.
   */
  enabled?: boolean
}

/**
 * The signed-in customer's saved embroidery settings, turned into items that could be selected, so
 * a blank modal can open with what they chose last time already in it.
 *
 * "Preferences" here means their own account settings, not a team store's Placements & Preferences
 * — those arrive as the garment's anatomy overlay.
 *
 * TODO: this is just scaffolding at this point, not consumed anywhere. Will adjust this when
 * preferences and multi-placement are both enabled.
 */
function useEmbroideryPreferencesImpl(props?: EmbroideryPreferencesProviderProps) {
  const enabled = props?.enabled ?? false
  const { constraints, productEmbroideryConfig } = useEmbroideryGarment()
  const { transientLogo, userLogos, status: logosStatus } = useEmbroideryLogos()

  const { loading, savedEmbroideryPreferences } = useProductScopedEmbroideryPreferences({
    enableEmbroideryPreferences: enabled,
    productEmbroideryConfig,
    transientLogo,
    userLogos,
    // Product and form defaults are deliberately withheld. A preference should carry only what the
    // customer actually saved; the form fills the rest in, so a default never looks like a choice.
    defaultFormValues: undefined,
    productDefaultFormValuesByKind: {},
  })

  // Every call mints fresh ids, so `constraints` has to stay reference-stable for an id read off a
  // preference item to still address it a render later.
  const preferenceItems = useMemo(
    () => itemsFromPreferences(savedEmbroideryPreferences, constraints),
    [savedEmbroideryPreferences, constraints]
  )

  return {
    savedEmbroideryPreferences,
    preferenceItems,
    isLoading: loading || logosStatus === 'pending',
    isEnabled: enabled,
    // The account's logos, fetched once here so the logo editor doesn't fetch them again.
    userLogos,
    logosStatus,
  }
}

const EmbroideryPreferencesContainer = createContainer(useEmbroideryPreferencesImpl)

export const EmbroideryPreferencesProvider = EmbroideryPreferencesContainer.Provider
export const useSavedEmbroideryPreferences = EmbroideryPreferencesContainer.useContainer
